import React, { useEffect, useState } from 'react';
import { 
  Grid, 
  Paper, 
  Typography, 
  Box, 
  CircularProgress, 
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Button,
  Chip
} from '@mui/material';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import GroupIcon from '@mui/icons-material/Group';
import TimelineIcon from '@mui/icons-material/Timeline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import HandshakeIcon from '@mui/icons-material/Handshake';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

const MetricCard = ({ title, value, icon, subtitle, onClick, selected }) => (
  <Paper 
    elevation={2} 
    sx={{ 
      p: 1.5, 
      height: '100%', 
      cursor: 'pointer',
      bgcolor: selected ? 'action.selected' : 'background.paper',
      '&:hover': {
        bgcolor: 'action.hover',
      },
    }}
    onClick={onClick}
  >
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
        {icon}
        <Typography variant="subtitle2" sx={{ ml: 1, fontSize: '0.875rem', fontWeight: 600 }}>
          {title}
        </Typography>
      </Box>
      <Typography variant="h5" sx={{ mb: 0.5, fontSize: '1.5rem', lineHeight: 1.2, wordBreak: 'break-word' }}>
        {value}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem', lineHeight: 1.2, mt: 'auto' }}>
        {subtitle}
      </Typography>
    </Box>
  </Paper>
);

const PartnerOpportunities = () => {
  const [opportunities, setOpportunities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [metrics, setMetrics] = useState({
    totalPipeline: 0,
    activePartners: 0,
    avgDealSize: 0,
    winRate: 0,
    unassignedOpps: 0,
    partnerIdentifiedPipeline: 0,
    closedWonPartnerPipeline: 0
  });
  const [quarterlyData, setQuarterlyData] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [activeFilter, setActiveFilter] = useState(null);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleFilterClick = (filter) => {
    setActiveFilter(activeFilter === filter ? null : filter);
    setPage(0);
  };

  const getFilteredOpportunities = () => {
    let filtered = [...opportunities];
    
    switch(activeFilter) {
      case 'totalPipeline':
        filtered = opportunities.filter(opp => !['Closed Won', 'Closed Lost', 'Adopt', 'Expand', 'Disqualified'].includes(opp.stage));
        break;
      case 'partnerIdentified':
        filtered = opportunities.filter(opp => 
          !['Closed Won', 'Closed Lost', 'Adopt', 'Expand', 'Disqualified'].includes(opp.stage) &&
          (opp.is_deal_registration === true || opp.lead_source === 'Partner')
        );
        break;
      case 'closedPartner':
        filtered = opportunities.filter(opp => 
          opp.stage === 'Closed Won' &&
          (opp.is_deal_registration === true || opp.lead_source === 'Partner')
        );
        break;
      case 'unassignedOpps':
        filtered = opportunities.filter(opp => 
          !['Closed Won', 'Closed Lost', 'Adopt', 'Expand', 'Disqualified'].includes(opp.stage) &&
          !opp.partner_account
        );
        break;
      default:
        // No filter applied
        break;
    }
    
    return filtered;
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        console.log('Fetching opportunities data...');
        const response = await fetch('https://sheets-dashboard-backend.gardner-johnson.workers.dev/api/opportunities');
        console.log('Response status:', response.status);
        console.log('Response headers:', Object.fromEntries(response.headers.entries()));
        
        if (!response.ok) {
          const errorText = await response.text();
          console.error('Response error:', errorText);
          throw new Error(`HTTP error! status: ${response.status} - ${errorText}`);
        }
        
        const data = await response.json();
        console.log('Response data structure:', Object.keys(data));
        
        if (!data.success) {
          throw new Error(data.error || 'Failed to fetch opportunities');
        }
        
        if (!Array.isArray(data.opportunities)) {
          console.error('Invalid opportunities data:', data);
          throw new Error('Invalid opportunities data received');
        }
        
        console.log(`Received ${data.opportunities.length} opportunities`);
        
        // Filter out unwanted stages for pipeline metrics
        const excludedStages = ['Adopt', 'Expand', 'Disqualified', 'Closed Won', 'Closed Lost'];
        const activeOpportunities = data.opportunities.filter(opp => 
          !excludedStages.includes(opp.stage)
        );
        
        // Filter opportunities for quarterly charts (include Closed Won)
        const chartExcludedStages = ['Adopt', 'Expand', 'Disqualified', 'Closed Lost'];
        const quarterlyOpportunities = data.opportunities.filter(opp => 
          !chartExcludedStages.includes(opp.stage)
        );
        
        setOpportunities(data.opportunities || []);
        
        // Calculate metrics (using activeOpportunities - excludes Closed Won)
        const pipeline = activeOpportunities.reduce((sum, opp) => sum + (parseFloat(opp.amount) || 0), 0);
        const uniquePartners = new Set(activeOpportunities.map(opp => opp.partner_account)).size;
        const avgDeal = pipeline / (activeOpportunities.length || 1);
        const closedWon = data.opportunities.filter(opp => opp.stage === 'Closed Won').length;
        const closedTotal = data.opportunities.filter(opp => opp.stage.startsWith('Closed')).length;
        const winRate = closedTotal ? (closedWon / closedTotal) * 100 : 0;
        const unassignedOpps = activeOpportunities.filter(opp => !opp.partner_account).length;
        
        // Calculate partner identified pipeline (deals with registration or partner lead source)
        const partnerIdentifiedPipeline = activeOpportunities
          .filter(opp => opp.is_deal_registration === true || opp.lead_source === 'Partner')
          .reduce((sum, opp) => sum + (parseFloat(opp.amount) || 0), 0);

        // Calculate closed won partner identified pipeline
        const closedWonPartnerPipeline = data.opportunities
          .filter(opp => 
            opp.stage === 'Closed Won' &&
            (opp.is_deal_registration === true || opp.lead_source === 'Partner')
          )
          .reduce((sum, opp) => sum + (parseFloat(opp.amount) || 0), 0);

        setMetrics({
          totalPipeline: pipeline,
          activePartners: uniquePartners,
          avgDealSize: avgDeal,
          winRate: winRate,
          unassignedOpps: unassignedOpps,
          partnerIdentifiedPipeline: partnerIdentifiedPipeline,
          closedWonPartnerPipeline: closedWonPartnerPipeline
        });

        // Calculate quarterly data with stage breakdown (using quarterlyOpportunities - includes Closed Won)
        const currentDate = new Date('2025-01-23'); // Using the provided current date
        
        // Function to get fiscal quarter from date
        const getFiscalQuarter = (date) => {
          const d = new Date(date);
          // Adjust month to fiscal year (Feb-Jan)
          const month = (d.getMonth() + 11) % 12; // Shift January to be last month
          const fiscalQuarter = Math.floor(month / 3) + 1;
          const year = d.getFullYear() + (d.getMonth() <= 0 ? 0 : 1); // Increment year if after January
          return { quarter: fiscalQuarter, year };
        };

        // Get current fiscal quarter
        const currentFQ = getFiscalQuarter(currentDate);
        
        // Initialize quarters data structure for current and next 3 quarters
        const quarterlyPipeline = {};
        
        for (let i = 0; i < 4; i++) {
          let quarter = (currentFQ.quarter + i - 1) % 4 + 1;
          let year = currentFQ.year + Math.floor((currentFQ.quarter + i - 1) / 4);
          const quarterKey = `Q${quarter}-${year}`;
          quarterlyPipeline[quarterKey] = {
            quarter: quarterKey,
            stages: {}
          };
        }

        // Group opportunities by quarter and stage (including Closed Won)
        quarterlyOpportunities.forEach(opp => {
          if (opp.close_date) {
            const { quarter, year } = getFiscalQuarter(opp.close_date);
            const quarterKey = `Q${quarter}-${year}`;
            
            if (quarterlyPipeline.hasOwnProperty(quarterKey)) {
              const amount = parseFloat(opp.amount) || 0;
              const stage = opp.stage;
              if (!quarterlyPipeline[quarterKey].stages[stage]) {
                quarterlyPipeline[quarterKey].stages[stage] = 0;
              }
              quarterlyPipeline[quarterKey].stages[stage] += amount;
            }
          }
        });

        // Transform data for charts
        const quarterlyChartData = Object.values(quarterlyPipeline).map(quarter => ({
          quarter: quarter.quarter,
          data: Object.entries(quarter.stages).map(([stage, amount]) => ({
            stage,
            amount
          })).sort((a, b) => {
            // Always put Closed Won at the end
            if (a.stage === 'Closed Won') return 1;
            if (b.stage === 'Closed Won') return -1;
            
            // Sort other stages in order
            const stageOrder = {
              '0- Research': 1,
              '1- Discovery': 2,
              '2- Scope': 3,
              '3- Tech Validation': 4,
              '4- Business Case Alignment': 5,
              '5- Negotiation': 6
            };
            return (stageOrder[a.stage] || 99) - (stageOrder[b.stage] || 99);
          })
        }));

        setQuarterlyData(quarterlyChartData);

      } catch (error) {
        console.error('Error fetching opportunity data:', error);
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      notation: 'compact',
      maximumFractionDigits: 1
    }).format(value);
  };

  return (
    <Box p={3}>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loading ? (
        <Box display="flex" justifyContent="center">
          <CircularProgress />
        </Box>
      ) : (
        <>
          {/* Key Metrics */}
          <Grid container spacing={3} mb={4}>
            <Grid item xs={12} sm={6} md={1.7}>
              <MetricCard
                title="Total Pipeline"
                value={formatCurrency(metrics.totalPipeline)}
                icon={<AttachMoneyIcon color="primary" />}
                subtitle="Current opportunity value"
                onClick={() => handleFilterClick('totalPipeline')}
                selected={activeFilter === 'totalPipeline'}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={1.7}>
              <MetricCard
                title="Partner Identified"
                value={formatCurrency(metrics.partnerIdentifiedPipeline)}
                icon={<HandshakeIcon color="primary" />}
                subtitle="Partner sourced deals"
                onClick={() => handleFilterClick('partnerIdentified')}
                selected={activeFilter === 'partnerIdentified'}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={1.7}>
              <MetricCard
                title="Closed Partner"
                value={formatCurrency(metrics.closedWonPartnerPipeline)}
                icon={<CheckCircleIcon color="success" />}
                subtitle="Closed won partner deals"
                onClick={() => handleFilterClick('closedPartner')}
                selected={activeFilter === 'closedPartner'}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={1.7}>
              <MetricCard
                title="Active Partners"
                value={metrics.activePartners}
                icon={<GroupIcon color="primary" />}
                subtitle="Partners with open opportunities"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={1.7}>
              <MetricCard
                title="Avg Deal Size"
                value={formatCurrency(metrics.avgDealSize)}
                icon={<TrendingUpIcon color="primary" />}
                subtitle="Average opportunity value"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={1.7}>
              <MetricCard
                title="Win Rate"
                value={`${metrics.winRate.toFixed(1)}%`}
                icon={<TimelineIcon color="primary" />}
                subtitle="Opportunity success rate"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={1.7}>
              <MetricCard
                title="Unassigned Opps"
                value={metrics.unassignedOpps}
                icon={<ErrorOutlineIcon color="warning" />}
                subtitle="Opportunities without partners"
                onClick={() => handleFilterClick('unassignedOpps')}
                selected={activeFilter === 'unassignedOpps'}
              />
            </Grid>
          </Grid>

          {/* Quarterly Pipeline Charts */}
          <Grid container spacing={3} mb={4}>
            {quarterlyData.map((quarter, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <Paper elevation={2} sx={{ p: 2 }}>
                  <Typography variant="h6" gutterBottom>
                    {quarter.quarter}
                  </Typography>
                  <Box sx={{ width: '100%', height: 200 }}>
                    <ResponsiveContainer>
                      <BarChart data={quarter.data}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="stage" angle={-45} textAnchor="end" height={80} />
                        <YAxis />
                        <Tooltip formatter={(value) => formatCurrency(value)} />
                        <Bar dataKey="amount" fill="#2196f3" />
                      </BarChart>
                    </ResponsiveContainer>
                  </Box>
                </Paper>
              </Grid>
            ))}
          </Grid>

          {/* Opportunities Table */}
          <Box mb={2} display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">
              Opportunities {activeFilter && '(Filtered)'}
            </Typography>
            {activeFilter && (
              <Button 
                variant="outlined" 
                size="small" 
                onClick={() => setActiveFilter(null)}
              >
                Clear Filter
              </Button>
            )}
          </Box>
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Partner</TableCell>
                  <TableCell>Customer</TableCell>
                  <TableCell>Stage</TableCell>
                  <TableCell align="right">Amount</TableCell>
                  <TableCell>Close Date</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {getFilteredOpportunities()
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((opp, index) => (
                    <TableRow key={index}>
                      <TableCell>{opp.partner_account || '-'}</TableCell>
                      <TableCell>{opp.account_name || '-'}</TableCell>
                      <TableCell>
                        <Chip 
                          label={opp.stage} 
                          size="small"
                          color={opp.stage === 'Closed Won' ? 'success' : 'default'}
                        />
                      </TableCell>
                      <TableCell align="right">{formatCurrency(opp.amount)}</TableCell>
                      <TableCell>
                        {opp.close_date ? new Date(opp.close_date).toLocaleDateString() : '-'}
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
            <TablePagination
              rowsPerPageOptions={[10, 25, 50]}
              component="div"
              count={getFilteredOpportunities().length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </TableContainer>

          {/* Partner Notes */}
          <Paper elevation={2} sx={{ mt: 4, p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Partner Notes
            </Typography>
            {activeFilter && getFilteredOpportunities().length > 0 ? (
              <>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle1" color="primary" gutterBottom>
                    Deal Source Breakdown
                  </Typography>
                  <Typography variant="body2">
                    • Deal Registration: {
                      getFilteredOpportunities().filter(opp => opp.is_deal_registration).length
                    } opportunities
                  </Typography>
                  <Typography variant="body2">
                    • Partner Lead Source: {
                      getFilteredOpportunities().filter(opp => opp.lead_source === 'Partner').length
                    } opportunities
                  </Typography>
                </Box>

                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle1" color="primary" gutterBottom>
                    Partner Tier Information
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Partner tier information will determine the following margins:
                  </Typography>
                  <Typography variant="body2">
                    • Gold Tier: 30% margin on all products
                  </Typography>
                  <Typography variant="body2">
                    • Silver Tier: 25% margin on all products
                  </Typography>
                  <Typography variant="body2">
                    • Bronze Tier: 20% margin on all products
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="subtitle1" color="primary" gutterBottom>
                    Product Margin Details
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Additional margins based on product type:
                  </Typography>
                  <Typography variant="body2">
                    • Enterprise License: +5% additional margin
                  </Typography>
                  <Typography variant="body2">
                    • Professional Services: Standard partner tier margin
                  </Typography>
                  <Typography variant="body2">
                    • Training & Certification: +2% additional margin
                  </Typography>
                </Box>
              </>
            ) : (
              <Typography variant="body2" color="text.secondary">
                Select a metric card above to view detailed partner information for filtered opportunities.
              </Typography>
            )}
          </Paper>
        </>
      )}
    </Box>
  );
};

export default PartnerOpportunities;
